import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MessageCircleIcon, XIcon } from 'lucide-react';
import { formatPrice } from '../utils/format';
import { productOrderUrl, WHATSAPP_DISPLAY } from '../utils/whatsapp';
import { trackAddToCart } from '../utils/metaPixel';
import type { Product } from '../types/catalog';
interface ProductDialogProps {
  product: Product | null;
  onClose: () => void;
}
export function ProductDialog({
  product,
  onClose
}: ProductDialogProps) {
  useEffect(() => {
    if (!product) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [product, onClose]);
  return <AnimatePresence data-id="element-81" data-mp="37n5rygmafbwd">
      {product && <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-label={`Ficha de ${product.name}`} data-id="element-82" data-mp="37jrmv0m8ool8">
          <motion.button type="button" aria-label="Cerrar ficha" onClick={onClose} className="absolute inset-0 h-full w-full cursor-default bg-navy/60" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-83" data-mp="y6afm74i0s2f" />

          <motion.div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-y-auto rounded-t-3xl bg-ivory sm:max-h-[88vh] sm:rounded-3xl" initial={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} transition={{
        duration: 0.26,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-84" data-mp="1gx1svjrlx2m3">
            <button type="button" onClick={onClose} aria-label="Cerrar ficha" className="absolute right-3 top-3 z-10 rounded-full bg-ivory/90 p-2 text-navy transition-colors duration-200 ease-smooth hover:bg-navy hover:text-ivory focus:outline-none focus-visible:ring-2 focus-visible:ring-cobalt" data-id="element-85" data-mp="1szuquom2hb6f">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-86" data-mp="27q20qfy0dlvi" />
            </button>

            <div className="grid sm:grid-cols-2" data-id="element-87" data-mp="2cn28qwef4yp9">
              <img src={product.image} alt={`Frasco de ${product.name}`} className="aspect-[4/5] w-full bg-white object-contain" data-id="element-88" data-mp="2godwewdqf9ut" />

              <div className="flex flex-col p-6 sm:p-8" data-id="element-89" data-mp="2qnmqdp8lkoh6">
                <p className="text-[11px] font-bold uppercase tracking-wide text-cobalt" data-id="element-90" data-mp="2ql0ifohso6yf">
                  {product.family}
                </p>
                <h2 className="mt-2 font-display text-3xl font-extrabold leading-tight text-navy" data-id="element-91" data-mp="w8k749a562mp">
                  {product.name}
                </h2>
                <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-mute" data-id="element-92" data-mp="ornl58q5x6i3">
                  {product.size} · {product.concentration}
                </p>

                <p className="mt-4 text-sm leading-relaxed text-mute" data-id="element-93" data-mp="f0j84ek1rvpw">{product.description}</p>

                <dl className="mt-5 space-y-2 border-t border-line pt-5 text-sm" data-id="element-94" data-mp="a1zio468w1ss">
                  {[{
                label: 'Salida',
                values: product.notes.salida
              }, {
                label: 'Corazón',
                values: product.notes.corazon
              }, {
                label: 'Fondo',
                values: product.notes.fondo
              }].map(group => <div key={group.label} className="flex gap-4" data-id="element-95" data-mp="srek3zpuesxp">
                      <dt className="w-20 shrink-0 text-[11px] font-semibold uppercase tracking-wide text-cobalt" data-id="element-96" data-mp="sqwbe3lzum8w">
                        {group.label}
                      </dt>
                      <dd className="text-navy" data-id="element-97" data-mp="3dftvt94u6gcw">{group.values.join(', ')}</dd>
                    </div>)}
                </dl>

                <p className="mt-5 border-t border-line pt-5 text-xs leading-relaxed text-mute" data-id="element-98" data-mp="1awwu78ujeuyq">
                  <span className="font-semibold uppercase tracking-wide" data-id="element-99" data-mp="w80p9kns1y2c">Ingredientes · </span>
                  {product.ingredients.join(' · ')}
                </p>

                <div className="mt-6" data-id="element-100" data-mp="117dak0z02x2t">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-cobalt" data-id="element-101" data-mp="1owxv2oasy2e7">
                    Precio de lanzamiento
                  </p>
                  <p className="mt-1 flex items-baseline gap-3" data-id="element-102" data-mp="1tuzoo35utk3x">
                    <span className="font-display text-3xl font-extrabold text-navy" data-id="element-103" data-mp="1eza140l0syx3">
                      {formatPrice(product.price)}
                    </span>
                    {product.compareAtPrice && <span className="text-base text-mute line-through" data-id="element-104" data-mp="3ik0cwzd8zav">
                        {formatPrice(product.compareAtPrice)}
                      </span>}
                  </p>
                </div>

                <div className="mt-4 flex flex-col gap-3" data-id="element-105" data-mp="2msa6mpvuyyuj">
                  <a href={productOrderUrl(product.name)} target="_blank" rel="noreferrer" onClick={() => trackAddToCart({
                contentName: product.name,
                contentId: product.slug,
                value: product.price
              })} className="flex w-full items-center justify-center gap-2 rounded-full bg-lime px-6 py-4 text-xs font-bold uppercase tracking-wide text-navy transition-colors duration-200 ease-smooth hover:bg-lime-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-cobalt" data-id="element-106" data-mp="2hq1dntizjwsf">
                    <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-107" data-mp="1k9vxnrmt432c" />
                    Pedir por WhatsApp
                  </a>
                  <p className="text-center text-[11px] text-mute" data-id="element-108" data-mp="3ff4ygvhlimpy">
                    Te atendemos al {WHATSAPP_DISPLAY}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}